import React, { useEffect, useRef, useState, useContext } from "react";
import { motion } from "framer-motion";
import { SafeAreaContext } from "../../App";
import { useNavigate, useOutletContext } from "react-router-dom";
import FilterBox from "./modal/filter";
import SearchBox from "./modal/search";
import SubscriptionModal from "./modal/UploadSignal";
import NewPackages from "./component/NewPackages";
import Targeted from "./component/Targeted";
import SpecialPackages from "./component/SpecialPackages";
import { IoMdNotifications } from "react-icons/io";
import { SlOptions } from "react-icons/sl";
import { Target } from "lucide-react";
import { FaPlus, FaChartBar, FaArrowRight } from "react-icons/fa6";
import AddIcon from "../body/Icons/AddIcon";
import FilterIcon from "../body/Icons/FilterIcon";
import SearchIcon from "../body/Icons/SearchIcon";
import { FaSignal } from "react-icons/fa6";
import { LuPackagePlus } from "react-icons/lu";
import SignalsService from "../../services/Signals";
import { FaBoxOpen } from "react-icons/fa";

export default function SignalPage() {
  const { setSafeAreaColor } = useContext(SafeAreaContext);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [searchBoxOpen, setSearchBoxOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [recentSearches, setRecentSearches] = useState(() =>
    JSON.parse(localStorage.getItem("recentSearches") || "[]"),
  );
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [signals, setSignals] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(true);
  const filterDropdownRef = useRef(null);
  const actionMenuRef = useRef(null);
  const searchBoxRef = useRef(null);
  const mainContentRef = useRef(null);

    const { setIsOpen } = useOutletContext();


  const updateSelectedFiltersFromCheckbox = (name, type, checked) => {
    if (checked) {
      setSelectedFilters((prev) => [...prev, { name, type }]);
    } else {
      setSelectedFilters((prev) =>
        prev.filter(
          (filter) => !(filter.name === name && filter.type === type),
        ),
      );
    }
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setFilterDropdownOpen(false);
    fetchSignals([]);
  };

  const applyFilters = () => {
    setFilterDropdownOpen(false);
    fetchSignals(selectedFilters);
  };

  const closeFilterDropdown = () => {
    setFilterDropdownOpen(false);
  };

  const fetchSignals = async (filters = []) => {
    setSignalsLoading(true);

    try {
      const params = {};

      const isFreeSelected = filters.some(
        (filter) => filter.name === "free" && filter.type === "general",
      );

      if (isFreeSelected) {
        params.isPremium = false;
      }

      const response = await SignalsService.getAll(params);
      setSignals(response?.data?.data ?? []);
    } catch {
      setSignals([]);
    } finally {
      setSignalsLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
  }, []);

  useEffect(() => {
    setSafeAreaColor({ background: "#FFFFFF" });
  }, []);

  // Close menus on outside click
  useEffect(() => {
    function handleClick(e) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(false);
      }
      if (
        filterDropdownOpen &&
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target)
      ) {
        setFilterDropdownOpen(false);
      }
      if (
        searchBoxOpen &&
        searchBoxRef.current &&
        !searchBoxRef.current.contains(e.target)
      ) {
        setSearchBoxOpen(false);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [filterDropdownOpen, searchBoxOpen]);

  // Adjust padding dynamically
  useEffect(() => {
    const basePadding = 100;
    const filterHeight = selectedFilters.length > 0 ? 40 : 0;
    const main = mainContentRef.current;
    if (!main) return;
    if (filterDropdownOpen || searchBoxOpen) main.style.paddingTop = "10px";
    else main.style.paddingTop = `${basePadding + filterHeight}px`;
  }, [filterDropdownOpen, searchBoxOpen, selectedFilters.length]);

  const checkSubscription = (button) => {
    // const expirationDate = new Date(
    //   userData?.expiration != null && userData?.expiration,
    // );
    // const now = new Date();
    // const hasSubscription =
    //   userData?.expiration != null && expirationDate >= now;
    toggleActionMenu();
    // if (!hasSubscription) setSubscriptionModalOpen(true);
    // else
    if (button === "signal") navigate("/createSignal");
    else if (button === "package")
      navigate("/createSignal?openCreatePackage=true");
  };

  const toggleActionMenu = (e) => {
    e?.stopPropagation();
    setActionMenuOpen((prev) => !prev);
  };

  const showFilterBox = () => {
    setActionMenuOpen(false);
    setFilterDropdownOpen(true);
    setSearchBoxOpen(false);
  };

  const showSearchBox = () => {
    setActionMenuOpen(false);
    setSearchBoxOpen(true);
    setFilterDropdownOpen(false);
    setTimeout(() => document.getElementById("search-input-box")?.focus(), 100);
  };

  const hideSearchBox = () => setSearchBoxOpen(false);

  function addToRecentSearches(term) {
    setRecentSearches((prev) => {
      const updated = [term, ...prev.filter((item) => item !== term)].slice(
        0,
        5,
      );

      localStorage.setItem("recentSearches", JSON.stringify(updated));

      return updated;
    });
  }

  async function showSearchResults(term) {
    const keyword = term.trim();

    if (!keyword) {
      setSearchResults([]);
      setSearchResultsVisible(false);
      return;
    }

    setSearchResultsVisible(true);

    try {
      const response = await SignalsService.getAll({
        search: keyword,
        pagination: false,
      });

      setSearchResults(response?.data?.data ?? []);
    } catch {
      setSearchResults([]);
    }
  }

  function performSearchBox() {
    const term = searchInput.trim();

    if (term) {
      addToRecentSearches(term);
      showSearchResults(term);
    }
  }

  function selectSearchResult(type, name) {
    hideSearchBox();
    if (type === "trader") {
      if (!selectedFilters.some((f) => f.name === name))
        setSelectedFilters((prev) => [...prev, { name, type: "trader" }]);
    }
  }

  const navigate = useNavigate();

  function goToMySignals() {
    navigate("/mysignals");
  }

  return (
    <div className="bg-gray-50 font-sans min-h-screen">
      {authModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center px-5">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto mt-20 overflow-hidden">
            <div className="bg-gradient-to-r from-[#ff4040] to-[#e12d2d] p-6 text-center relative">
              <button
                onClick={() => setAuthModalOpen(false)}
                className="absolute top-4 right-4 text-white text-xl"
              >
                ×
              </button>
              <div className="bg-white bg-opacity-20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                ⚠️
              </div>
              <h2 className="text-lg font-bold text-white">
                Registration Required
              </h2>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-700 font-semibold mb-2">Access Denied</p>
              <p className="text-gray-600 text-sm mb-4">
                To view this section, please sign up or log in to continue.
              </p>
              <div className="space-y-3">
                <button className="w-full bg-gradient-to-r from-[#ff7b00] to-[#ff9c33] text-white py-3 rounded-lg font-semibold">
                  Sign Up
                </button>
                <button className="w-full border-2 border-[#ff7b00] text-[#ff7b00] py-3 rounded-lg font-semibold hover:bg-[#ff7b00] hover:text-white transition-all">
                  Log In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        subscriptionModalOpen={subscriptionModalOpen}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
      />

      {/* ========== HEADER ========== */}
      <header className="bg-white px-4 py-4 fixed top-0 left-0 right-0 shadow-sm z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="nav-icon text-2xl flex items-center justify-center w-8 h-8 relative">
              <div className="signal-glow"></div>
              <Target className="text-primary" size={25} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Signals</h1>
          </div>

          <div className="flex items-center space-x-3">
           <button onClick={() => setIsOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 hover:bg-orange-100 transition-colors">
              <IoMdNotifications className="size-5 text-orange-500"></IoMdNotifications>
            </button>

            <div className="relative" ref={actionMenuRef}>
              <button
                id="menu-toggle"
                onClick={toggleActionMenu}
                className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 transition-all duration-200 ${actionMenuOpen && "rotate-90"
                  }`}
              >
                <SlOptions className="text-[#878888]" />
              </button>

              {actionMenuOpen && (
                <div className="absolute animate-openSideBar right-0 top-12 bg-white rounded-xl border border-gray-200 shadow-lg min-w-[200px] z-50">
                  <div className="p-2">
                    <button
                      onClick={() => checkSubscription("signal")}
                      className="flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 hover:bg-gray-50"
                    >
                      <i className="fa-solid fa-plus text-primary w-4">
                        <AddIcon></AddIcon>
                      </i>
                      <span className="text-[15px]">Upload New Signal</span>
                    </button>
                    <button
                      onClick={() => navigate("/createPackage")}
                      className="flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 hover:bg-gray-50"
                    >
                      <i className="fa-solid fa-plus text-primary w-4">
                        <LuPackagePlus size={20} />
                      </i>
                      <span className="text-[15px]">Create Package</span>
                    </button>
                    <button
                      onClick={goToMySignals}
                      className="flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 hover:bg-gray-50"
                    >
                      <div className="text-primary font-bold pb-0.5">
                        <FaSignal></FaSignal>
                      </div>
                      <span className="text-[15px]">My Signals</span>
                    </button>
                    {/* <button
                      onClick={(e) => {
                        // e.stopPropagation();
                        // showFilterBox();
                      }}
                      className="flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 hover:bg-gray-50"
                    >
                      <i className="fa-solid fa-filter text-orange-500 w-4">
                        <FilterIcon></FilterIcon>
                      </i>
                      <span>Filter</span>
                    </button> */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        showSearchBox();
                      }}
                      className="flex w-full items-center space-x-3 rounded-lg px-3 py-2.5 hover:bg-gray-50"
                    >
                      <i className="fa-solid fa-search text-purple-500 w-4">
                        <SearchIcon></SearchIcon>
                      </i>
                      <span>Search</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Dropdowns */}
      {filterDropdownOpen && (
        <FilterBox
          filterDropdownRef={filterDropdownRef}
          selectedFilters={selectedFilters}
          updateSelectedFiltersFromCheckbox={updateSelectedFiltersFromCheckbox}
          clearAllFilters={clearAllFilters}
          applyFilters={applyFilters}
          closeFilterDropdown={closeFilterDropdown}
        />
      )}
      {searchBoxOpen && (
        <SearchBox
          searchBoxRef={searchBoxRef}
          hideSearchBox={hideSearchBox}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          showSearchResults={showSearchResults}
          setSearchResultsVisible={setSearchResultsVisible}
          searchResults={searchResults}
          searchResultsVisible={searchResultsVisible}
          recentSearches={recentSearches}
          addToRecentSearches={addToRecentSearches}
          performSearchBox={performSearchBox}
          selectSearchResult={selectSearchResult}
        />
      )}

      {/* ========== MAIN CONTENT ========== */}
      <main ref={mainContentRef} className="pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full overflow-hidden"
        >
          {/* Hero */}
          <section className="px-4 py-6">
            <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
              <div className="relative z-10">
                <h2 className="text-2xl font-bold mb-2">
                  Welcome to Signal Pro
                </h2>
                <p className="text-white/80 mb-4">
                  sell premium trading signals & insights
                </p>
                <button
                  className="bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    navigate("/renew");
                  }}
                >
                  Get Started
                </button>
              </div>
            </div>
          </section>

          {/* Stats */}
          {/* <section className="px-4 pb-6 grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <FaChartLine size={20} className="text-[#4CAF50]"/>
                                <span className="text-xs bg-[#e6f7e6] text-[#4CAF50] px-2 py-1 rounded-full">
                  +12.5%
                </span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">1,234</p>
                            <p className="text-sm text-gray-500">Successful Signals</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <FaUsers size={20} className="text-[#007bff]"/>
                                <span className="text-xs bg-[#e6f0ff] text-[#007bff] px-2 py-1 rounded-full">
                  +5.2%
                </span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">8,921</p>
                            <p className="text-sm text-gray-500">Active Users</p>
                        </div>
                    </section> */}

          {/* My Latest Signals */}
          <section className="px-4 pb-4">
            <button
              onClick={() => navigate("/my-latest-signals")}
              className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FaSignal className="text-primary text-xl" />
                </div>

                <div className="text-left">
                  <h3 className="font-bold text-gray-900">
                    My Latest Signals
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Your subscribed signals feed
                  </p>
                </div>
              </div>

              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                <FaArrowRight className="text-primary" size={13} />
              </div>
            </button>
          </section>

          {/* Components */}
          <NewPackages />
          {/* <Targeted signals={signals} loading={signalsLoading} />         */}
          {/* <RecommendedForYou /> */}
          <SpecialPackages />

          {/* Quick Actions */}
          <section className="px-4 py-6" dir="ltr">
            <h3 className="mb-4 text-lg font-bold text-gray-900">
              Quick Actions
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate("/createSignal")}
                className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="mb-3 flex items-center justify-between">
                  <FaPlus className="text-primary" size={20} />

                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
                    <FaArrowRight className="text-primary" size={12} />
                  </div>
                </div>

                <div className="space-y-1 text-left">
                  <p className="font-semibold text-gray-800">Add Signal</p>
                  <p className="text-xs text-gray-500">Upload new signal</p>
                </div>
              </button>


              <button
                onClick={() => navigate("/createPackage")}
                className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="mb-3 flex items-center justify-between">
                  <FaBoxOpen className="text-primary" size={20} />

                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
                    <FaArrowRight className="text-primary" size={12} />
                  </div>
                </div>

                <div className="space-y-1 text-left">
                  <p className="font-semibold text-gray-800">Add Package</p>
                  <p className="text-xs text-gray-500">Create new package</p>
                </div>
              </button>
            </div>
          </section>
        </motion.div>
      </main>
    </div>
  );
}
